import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import moment from 'moment';
import { IOptionType } from '@zz2/zz2-ui';
import { IAgreementCode } from './agreementCode';
import { IAgreementCodeFormValues } from './agreementCodeFormValues';
import { IAgreementCodeUpsert } from './agreementCodeUpsert';
import { ICommodity } from '../commodity/commodity';
import { ICountry } from '../country/country';
import { IMarket } from '../market/market';

export default class AgreementCodeModelHelper {
    public static createFormValues = (
        agreementCode : IAgreementCode | null,
        markets : Array<IMarket>,
        commodities : Array<ICommodity>,
        countries : Array<ICountry>
    ) : IAgreementCodeFormValues => {
        if (!agreementCode) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            name: '',
            description: null,
            targetMarket: null,
            startDate: moment.utc().local(),
            endDate: moment.utc().local().year(3),
            commodities: [],
            countries: [],
            isActive: true,
        };

        const marketOption = markets.find(x => x.id === agreementCode.targetMarketId);

        const commodityOptions = commodities
            .filter(x => x.isActive && agreementCode.commodityIds.includes(x.id))
            .map(x => { // TODO: OptionType.FromDataModel
                return {
                    value: x.id,
                    label: `(${x.code}) - ${x.name}`,
                }
            });

        const countryOptions = countries
            .filter(x => x.isActive && agreementCode.countryIds.includes(x.id))
            .map(x => { // TODO: OptionType.FromDataModel
                return {
                    value: x.id,
                    label: `(${x.code}) - ${x.name}`,
                }
            });

        return {
            id: agreementCode.id,
            guid: agreementCode.guid,
            code: agreementCode.code,
            name: agreementCode.name,
            description: agreementCode.description,
            targetMarket: marketOption != null
                ? { // TODO: OptionType.FromDataModel
                    value: marketOption?.id,
                    label: `(${marketOption.code}) - ${marketOption.name}`,
                }
                :
                null,
            startDate: moment.utc(agreementCode.startDate).local(),
            endDate: moment.utc(agreementCode.endDate).local(),
            commodities: commodityOptions,
            countries: countryOptions,
            isActive: agreementCode.isActive,
        };
    };

    public static createUpsert = (formValues : IAgreementCodeFormValues) : IAgreementCodeUpsert => {
        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            name: formValues.name,
            description: formValues.description?.trim() !== ''
                ? (formValues.description?.trim() ?? null)
                : null,
            targetMarketId: formValues.targetMarket != null ? Number(formValues.targetMarket.value) : null,
            startDate: formValues.startDate,
            endDate: formValues.endDate,
            commodityIds: formValues.commodities.map(x => Number(x.value)),
            countryIds: formValues.countries.map(x => Number(x.value)),
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<IAgreementCodeFormValues> => Yup.object().shape({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        name: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        description: Yup.string().trim().defined().nullable(),
        startDate: Yup.mixed<moment.Moment>().defined().required('Required'),
        endDate: Yup.mixed<moment.Moment>().defined().nullable(),
        targetMarket: Yup.mixed<IOptionType>().defined().nullable(),
        commodities: Yup.array<IOptionType>().defined().required('Required'),
        countries: Yup.array<IOptionType>().defined().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });
}